import type { CSSProperties, MouseEvent, RefObject } from "react";
import { useEffect, useId, useRef, useState } from "react";
import { cn, tv } from "tailwind-variants";
import { Heading } from "#/_packages/umbrella/components/ui/primitives/heading.tsx";
import { Button } from "#/_packages/umbrella/features/button/components/button.tsx";
import UmbSpriteIcon from "#/_packages/umbrella/features/icons/components/umb-sprite-icon.tsx";
import { toHoverItemCssClasses } from "#/_packages/umbrella/integrations/umbraco/utils/animation-utils.ts";
import type {
	AnimationScrollType,
	BoxVariant,
	ButtonVariant,
	HeadingLevel,
	Intent,
	Size,
} from "#/_packages/umbrella/types";
import { tryGetHeadingLevel } from "#/_packages/umbrella/utils";
import { m } from "#/paraglide/messages";

export type TocItem = {
	id: string;
	heading: string;
	level?: number;
};

export type TocDisplay = "flex" | "grid";
export type TocAlignmentSelfVertical = "start" | "center" | "end";
export type TocAnimationHoverItemTypes = string[];
export type TocAnimationScrollType = AnimationScrollType;
export type TocIntentColor = Intent;
export type TocSize = Size;
export type TocTypeVariant = ButtonVariant;

let activeScrollAnimation: number | undefined;

export type TableOfContentsProps = {
	id?: string | null;
	textLabel?: string | null;
	hideHeading?: boolean;
	boxVariant?: BoxVariant;
	display?: TocDisplay;
	alignmentSelfVertical?: TocAlignmentSelfVertical;
	collapse?: boolean;
	sticky?: boolean;
	navLabel?: string;
	headingLevel?: HeadingLevel;
	headingAppearance?: string | null;
	animationHoverType?: string[];
	animationScrollType?: AnimationScrollType;
	size?: Size;
	intentColor?: Intent;
	typeVariant?: ButtonVariant;
	iconSvgId?: string | null;
	iconPosition?: "start" | "end" | string | null;
	iconRotation?: string | number | null;
	tocLevels?: number[];
	className?: string;
};

const tableOfContents = tv({
	slots: {
		root: "block__submenu w-full",
		toggle: "btn-full-width w-full justify-center md:hidden",
		wrapper: "submenu-wrapper",
		nav: "submenu submenu-vertical",
		heading: "submenu-title mt-3 text-sm",
		section: "submenu-section list-none p-0 m-0",
		item: "nav-item relative relative",
		link: "nav-link focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring",
	},
	variants: {
		boxVariant: {
			none: { root: "" },
			background: {
				root: "card card-body card-background rounded-lg bg-gray-50 p-4",
			},
			outline: {
				root: "card card-body card-outline rounded-lg border border-gray-200 p-4",
			},
			shadow: {
				root: "card card-body card-shadow rounded-lg bg-white p-4 shadow-md",
			},
		},
		alignmentSelfVertical: {
			start: { root: "self-start" },
			center: { root: "self-center" },
			end: { root: "self-end" },
		},
		display: {
			flex: { section: "submenu-section-flex flex flex-col gap-2" },
			grid: { section: "submenu-section-grid grid gap-2" },
		},
		sticky: {
			true: { root: "sticky" },
		},
		animationScrollType: {
			none: { root: "" },
			"anim-view-fadeIn-moveUp": { root: "anim-view-fadeIn-moveUp" },
			"anim-view-fadeIn-moveDown": { root: "anim-view-fadeIn-moveDown" },
			"anim-view-fadeIn-moveLeft": { root: "anim-view-fadeIn-moveLeft" },
		},
	},
});

function getIndentClass(item: TocItem, enableIndent: boolean) {
	if (!enableIndent || !item.level) {
		return undefined;
	}

	if (item.level === 3) {
		return "ml-3";
	}

	if (item.level >= 4) {
		return "ml-4";
	}

	return undefined;
}

function slugify(text: string): string {
	return text
		.toLowerCase()
		.trim()
		.replace(/[\s._]+/g, "-")
		.replace(/[^a-z0-9-]/g, "")
		.replace(/-+/g, "-")
		.replace(/^-|-$/g, "");
}

function getHeadingLevel(heading: Element): number | undefined {
	const level = Number.parseInt(heading.tagName.replace(/^H/i, ""), 10);
	return Number.isNaN(level) ? undefined : level;
}

function getHeadingSelector(tocLevels: number[]) {
	const levels = tocLevels.filter((level) => level >= 1 && level <= 6);
	const resolvedLevels = levels.length > 0 ? levels : [2, 3];

	return resolvedLevels.map((level) => `h${level}`).join(",");
}

function ensureHeadingId(heading: HTMLElement, usedIds: Set<string>) {
	if (heading.id) {
		usedIds.add(heading.id);
		return heading.id;
	}

	const fallbackId = `heading-${usedIds.size + 1}`;
	const baseId = slugify(heading.textContent || "") || fallbackId;
	let id = baseId;
	let counter = 1;

	while (usedIds.has(id)) {
		id = `${baseId}-${counter++}`;
	}

	heading.id = id;
	usedIds.add(id);

	return id;
}

function areTocItemsEqual(currentItems: TocItem[], nextItems: TocItem[]) {
	if (currentItems.length !== nextItems.length) {
		return false;
	}

	return currentItems.every((currentItem, index) => {
		const nextItem = nextItems[index];

		return (
			currentItem.id === nextItem.id &&
			currentItem.heading === nextItem.heading &&
			currentItem.level === nextItem.level
		);
	});
}

function useTableOfContentsHeadings(
	rootRef: RefObject<HTMLElement | null>,
	tocLevels: number[],
) {
	const [headings, setHeadings] = useState<TocItem[]>([]);

	useEffect(() => {
		const root = rootRef.current;
		const grid = root?.closest(".grid");

		if (!root || !grid) {
			setHeadings([]);
			return;
		}

		const scanHeadings = () => {
			const selector = getHeadingSelector(tocLevels);
			const usedIds = new Set(
				Array.from(document.querySelectorAll<HTMLElement>("[id]")).map(
					(element) => element.id,
				),
			);

			const nextHeadings = Array.from(
				grid.querySelectorAll<HTMLElement>(selector),
			)
				.filter(
					(heading) =>
						!root.contains(heading) &&
						Boolean(
							root.compareDocumentPosition(heading) &
								Node.DOCUMENT_POSITION_FOLLOWING,
						),
				)
				.map((heading) => {
					const text = (heading.textContent || "").trim();

					if (!text) {
						return undefined;
					}

					const value: TocItem = {
						id: ensureHeadingId(heading, usedIds),
						heading: text,
						level: getHeadingLevel(heading),
					};

					return value;
				})
				.filter((item): item is TocItem => Boolean(item));

			setHeadings((currentHeadings) =>
				areTocItemsEqual(currentHeadings, nextHeadings)
					? currentHeadings
					: nextHeadings,
			);
		};

		scanHeadings();

		const observer = new MutationObserver(scanHeadings);
		observer.observe(grid, {
			childList: true,
			subtree: true,
			characterData: true,
		});

		return () => observer.disconnect();
	}, [rootRef, tocLevels]);

	return headings;
}

function getScrollableParent(element: HTMLElement) {
	let parent = element.parentElement;

	while (parent && parent !== document.body) {
		const style = window.getComputedStyle(parent);
		const overflowY = style.overflowY;
		const canScroll = /(auto|scroll|overlay)/.test(overflowY);

		if (canScroll && parent.scrollHeight > parent.clientHeight) {
			return parent;
		}

		parent = parent.parentElement;
	}

	return null;
}

function cssLengthToPixels(value: string) {
	const trimmedValue = value.trim();
	const numericValue = Number.parseFloat(trimmedValue);

	if (!Number.isFinite(numericValue)) {
		return undefined;
	}

	if (trimmedValue.endsWith("rem")) {
		const rootFontSize = Number.parseFloat(
			window.getComputedStyle(document.documentElement).fontSize,
		);

		return numericValue * rootFontSize;
	}

	if (trimmedValue.endsWith("px")) {
		return numericValue;
	}

	return undefined;
}

function getStickyHeaderOffset() {
	const styles = window.getComputedStyle(document.documentElement);
	const siteHeaderHeight = cssLengthToPixels(
		styles.getPropertyValue("--site-header-height") || "4.5rem",
	);

	return siteHeaderHeight ? siteHeaderHeight + 16 : 88;
}

function easeInOutCubic(value: number) {
	return value < 0.5
		? 4 * value * value * value
		: 1 - (-2 * value + 2) ** 3 / 2;
}

function animateScrollTo(
	scrollElement: Element | Window,
	top: number,
	behavior: ScrollBehavior,
) {
	const isWindow = scrollElement === window;
	const startTop = isWindow
		? window.scrollY
		: (scrollElement as Element).scrollTop;
	const distance = top - startTop;

	if (behavior !== "smooth" || Math.abs(distance) < 1) {
		if (isWindow) {
			window.scrollTo({ top, behavior: "auto" });
		} else {
			(scrollElement as Element).scrollTo({ top, behavior: "auto" });
		}

		return;
	}

	const duration = 450;
	const startTime = window.performance.now();

	if (activeScrollAnimation) {
		window.cancelAnimationFrame(activeScrollAnimation);
	}

	const tick = (currentTime: number) => {
		const elapsed = currentTime - startTime;
		const progress = Math.min(elapsed / duration, 1);
		const nextTop = startTop + distance * easeInOutCubic(progress);

		if (isWindow) {
			window.scrollTo(0, nextTop);
		} else {
			(scrollElement as Element).scrollTop = nextTop;
		}

		if (progress < 1) {
			activeScrollAnimation = window.requestAnimationFrame(tick);
		} else {
			activeScrollAnimation = undefined;
		}
	};

	activeScrollAnimation = window.requestAnimationFrame(tick);
}

function scrollToHeading(event: MouseEvent<HTMLAnchorElement>, id: string) {
	const target = document.getElementById(id);

	if (!target) {
		return;
	}

	event.preventDefault();

	const behavior = window.matchMedia("(prefers-reduced-motion: reduce)").matches
		? "auto"
		: "smooth";
	const scrollableParent = getScrollableParent(target);
	const offset = getStickyHeaderOffset();

	if (scrollableParent) {
		const parentRect = scrollableParent.getBoundingClientRect();
		const targetRect = target.getBoundingClientRect();

		animateScrollTo(
			scrollableParent,
			scrollableParent.scrollTop + targetRect.top - parentRect.top - offset,
			behavior,
		);
	} else {
		animateScrollTo(
			window,
			target.getBoundingClientRect().top + window.scrollY - offset,
			behavior,
		);
	}

	window.history.pushState(null, "", `#${id}`);
}

function TableOfContentsHeading({
	headingLevel,
	headingAppearance,
	children,
}: {
	headingLevel: HeadingLevel;
	headingAppearance?: string | null;
	children: string;
}) {
	const { heading } = tableOfContents();

	return (
		<Heading
			level={headingLevel}
			className={cn(heading(), "text-xl!")}
			display={tryGetHeadingLevel(headingAppearance) ?? 4}
		>
			<span className="title">{children}</span>
		</Heading>
	);
}

export function TableOfContents({
	id,
	textLabel,
	hideHeading = false,
	boxVariant = "none",
	display = "flex",
	alignmentSelfVertical = "start",
	collapse = false,
	sticky = false,
	navLabel,
	headingLevel = 4,
	headingAppearance,
	animationHoverType = [],
	animationScrollType = "none",
	size = "medium",
	intentColor = "primary",
	typeVariant = "solid",
	iconSvgId,
	iconPosition = "end",
	iconRotation = 90,
	tocLevels = [2, 3],
	className,
}: TableOfContentsProps) {
	const rootRef = useRef<HTMLDivElement>(null);
	const [isOpen, setIsOpen] = useState(false);
	const headings = useTableOfContentsHeadings(rootRef, tocLevels);
	const generatedId = useId().replaceAll(":", "");
	const collapseId = `${id ?? `toc-${generatedId}`}-collapse`;
	const slots = tableOfContents({
		boxVariant,
		display,
		alignmentSelfVertical,
		sticky,
		animationScrollType,
	});
	const resolvedTextLabel = textLabel || m.table_of_contents_title();
	const resolvedNavLabel = navLabel || m.table_of_contents();
	const hasIcon = Boolean(iconSvgId);
	const hoverItemClasses = toHoverItemCssClasses(animationHoverType);
	const parsedRotation = Number(iconRotation);
	const resolvedRotation = Number.isFinite(parsedRotation)
		? parsedRotation
		: 90;
	const iconStyle: CSSProperties = {
		transform: isOpen ? `rotate(${resolvedRotation}deg)` : "rotate(0deg)",
	};
	const rootStyle: CSSProperties | undefined = sticky
		? { top: "calc(var(--site-header-height, 4.5rem) + 1rem)" }
		: undefined;

	const headingLevels = tocLevels.join(",");

	const itemLevels = headings
		.map((item) => item.level)
		.filter((level): level is number => typeof level === "number");
	const enableIndent =
		itemLevels.length > 0 && Math.max(...itemLevels) > Math.min(...itemLevels);

	const navContent = (
		<nav className={slots.nav()} aria-label={resolvedNavLabel}>
			{!hideHeading ? (
				<TableOfContentsHeading
					headingLevel={headingLevel}
					headingAppearance={headingAppearance}
				>
					{resolvedTextLabel}
				</TableOfContentsHeading>
			) : null}
			<ul className={slots.section()} data-toc-levels={headingLevels}>
				{headings.map((item) => (
					<li
						key={item.id}
						className={cn(slots.item(), getIndentClass(item, enableIndent))}
					>
						<a
							href={`#${item.id}`}
							className={cn(slots.link(), hoverItemClasses)}
							onClick={(event) => scrollToHeading(event, item.id)}
						>
							{item.heading}
						</a>
					</li>
				))}
			</ul>
		</nav>
	);

	return (
		<div
			ref={rootRef}
			id={id ?? undefined}
			className={cn(slots.root(), className)}
			style={rootStyle}
		>
			{collapse ? (
				<>
					<Button
						type="button"
						intent={intentColor}
						variant={typeVariant}
						size={size}
						className={cn(slots.toggle(), !isOpen && "collapsed", "mb-3")}
						aria-expanded={isOpen}
						aria-controls={collapseId}
						onPress={() => setIsOpen((current) => !current)}
					>
						{iconPosition === "start" && hasIcon && iconSvgId ? (
							<UmbSpriteIcon
								icon={iconSvgId}
								iconSize="small"
								className="icon-18 transition-transform duration-200"
								style={iconStyle}
							/>
						) : null}
						{!hideHeading ? <span>{resolvedTextLabel}</span> : null}
						{iconPosition === "end" && hasIcon && iconSvgId ? (
							<UmbSpriteIcon
								icon={iconSvgId}
								iconSize="small"
								className="icon-18 transition-transform duration-200"
								style={iconStyle}
							/>
						) : null}
					</Button>
					<div
						id={collapseId}
						className={cn(slots.wrapper(), !isOpen && "hidden md:block")}
					>
						{navContent}
					</div>
				</>
			) : (
				<div className={slots.wrapper()}>{navContent}</div>
			)}
		</div>
	);
}
